import { useTranslation } from 'react-i18next';
import { S, StatusBadge } from '../regureStyles';
import { useFeature } from '../../hooks/useAccess';

// Header keys, in column order. `feature` marks a column that only exists for
// tenants running that module — header and cell are dropped together so the
// row can never shift out of alignment with the head.
const HEADER_KEYS = [
  { key: 'screens.brokers.colName' },
  { key: 'screens.brokers.colContact' },
  { key: 'screens.brokers.colStatus' },
  { key: 'screens.brokers.colCommPlan', feature: 'commissions' },
  { key: 'screens.brokers.colPolicies' },
  { key: 'screens.brokers.colLocation' },
  { key: 'screens.brokers.colLossRatio' },
  { key: 'screens.brokers.colPortalAccess' },
];

// `onSelect` takes an ID, not the row object. The row object is the LIST
// projection and is deliberately not enough to build the detail card from —
// see openBrokerDetail in useBrokersFunctions. Passing the id forces every
// open through the one loader.
const Table = ({ paginated, onSelect }) => {
  const { t } = useTranslation();
  // Commissions off means the column is absent, not blanked: no header, no
  // cell, no placeholder. The detail card, the Portfolio Summary field and the
  // Add/Edit form were gated earlier; this column was the one left rendering a
  // dash for a module MOK does not have.
  const commissionsEnabled = useFeature('commissions');
  const headers = HEADER_KEYS.filter(h => !h.feature || (h.feature === 'commissions' && commissionsEnabled));
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map(({ key: h }) => (
            <th
              key={h}
              style={{
                padding: '13px 14px',
                textAlign: 'left',
                fontSize: 11,
                fontWeight: 700,
                color: S.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                borderBottom: `2px solid ${S.borderLight}`,
                whiteSpace: 'nowrap',
              }}
            >
              {t(h)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {paginated.map((b, i) => (
          <tr
            key={b.id}
            onClick={() => onSelect(b.id)}
            style={{ borderBottom: `1px solid ${S.borderLight}`, cursor: 'pointer', background: i % 2 === 0 ? '#fff' : S.bgCard, transition: 'background 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
            onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
          >
            <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 700, color: S.textPrimary }}>{b.name}</td>
            <td style={{ padding: '13px 14px' }}>
              <div style={{ fontSize: 13, color: S.textPrimary }}>{b.contact || '—'}</div>
              <div style={{ fontSize: 12, color: S.textMuted }}>{b.email || '—'}</div>
            </td>
            <td style={{ padding: '13px 14px' }}>
              <StatusBadge status={b.status} />
            </td>
            {commissionsEnabled && (
              <td style={{ padding: '13px 14px', fontSize: 13, color: S.burgundy, fontWeight: 600 }}>{b.commPlan || '—'}</td>
            )}
            {/* Authoritative count from listBrokers, not the unmaintained
                Broker.policies field that rendered 0 for every Sponsor. `== null`
                keeps a real 0 rendering as 0 and reserves the em dash for a
                genuinely missing value. */}
            <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
              {b.policyCount == null ? '—' : b.policyCount}
            </td>
            <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{[b.city, b.canton].filter(Boolean).join(', ') || '—'}</td>
            <td style={{ padding: '13px 14px' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: (b.lossRatio || 0) < 50 ? '#10b981' : (b.lossRatio || 0) < 65 ? '#f59e0b' : '#ef4444' }}>
                {b.lossRatio > 0 ? `${b.lossRatio}%` : '—'}
              </span>
            </td>
            <td style={{ padding: '13px 14px' }}>
              <span
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  background: b.status === 'active' ? '#dcfce7' : '#f1f5f9',
                  color: b.status === 'active' ? '#15803d' : '#64748b',
                }}
              >
                {b.status === 'active' ? t('screens.brokers.enabled') : t('screens.brokers.disabled')}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default Table;
